import React from 'react';

import {useSelector} from 'react-redux';

import TabbedModal, {
  StyledModalFooter,
} from 'app/components/general/TabbedModal';
import {useCurrentUserState} from 'app/current-user/api';
import {useAccountSettingsState} from 'app/settings/api';
import {showUpgradeModal, UPGRADE_MODALS} from 'app/toasts/slice';
import * as urls from 'app/urls';
import {gt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';

export function UpgradeModalContainer(): JSX.Element | null {
  const actions = useAsyncActions({showUpgradeModal});
  const {is_owner} = useCurrentUserState().data!;
  const {owner_email} = useAccountSettingsState().data!;
  const showingUpgradeModal = useSelector(
    (state) => state.toasts.showingUpgradeModal
  );

  if (showingUpgradeModal != null) {
    const ModalToShow = modalsMap[showingUpgradeModal];

    if (ModalToShow) {
      return (
        <ModalToShow
          show={!!ModalToShow}
          owner_email={owner_email}
          upgradeModal={showingUpgradeModal}
          isOwner={is_owner}
          onCancel={() => actions.showUpgradeModal(null)}
        />
      );
    }
  }

  return null;
}

interface IntermediateModalProps {
  upgradeModal: null | UPGRADE_MODALS;
  isOwner: boolean;
  owner_email: string;
  show: boolean;
  onCancel: () => unknown;
}

const UserLimitModal = (props: IntermediateModalProps) => (
  <BaseLimitModal
    title={gt('Upgrade to invite teammates')}
    confirmText={gt('Increase Teammate Limit')}
    {...props}
  >
    <p>
      {gt('You have reached the limit of teammates available on your account.')}
    </p>
    <p>{gt('Upgrade your account to invite new teammates.')}</p>
  </BaseLimitModal>
);

const ManagerLimitModal = (props: IntermediateModalProps) => (
  <BaseLimitModal
    title={gt('Upgrade to add managers')}
    confirmText={gt('Increase Manager Limit')}
    {...props}
  >
    <p>
      {gt('You have reached the limit of managers available on your account.')}
    </p>
    <p>{gt('Upgrade your account to add new managers.')}</p>
  </BaseLimitModal>
);

const ProcedureLimitModal = (props: IntermediateModalProps) => (
  <BaseLimitModal
    title={gt('Upgrade for unlimited procedures')}
    confirmText={gt('Get Unlimited Procedures')}
    {...props}
  >
    <p>
      {gt(
        'Looks like you are at your procedure limit.  The next plan includes an unlimited number of procedures as well as the ability to attach files to each step.'
      )}
    </p>
    <p>{gt('Upgrade your account to add new procedures.')}</p>
  </BaseLimitModal>
);

const TeamsLimitModal = (props: IntermediateModalProps): JSX.Element => (
  <BaseLimitModal
    title={gt('Upgrade to create new teams')}
    confirmText={gt('Increase Team Limit')}
    {...props}
  >
    <p>
      {gt('You have reached the limit of teams available on your account.')}
    </p>
    <p>{gt('Upgrade your account to create new teams.')}</p>
  </BaseLimitModal>
);

const CreateKBModal = (props: IntermediateModalProps): JSX.Element => (
  <BaseLimitModal
    title={gt('Upgrade to create a Knowledge Base')}
    confirmText={gt('Upgrade Plan Now')}
    {...props}
  >
    <p className="lead">
      {gt(
        'Knowledge Bases are available on pay per team member pricing plans.'
      )}
    </p>
    <p>{gt('Upgrade your account to create a Knowledge Base.')}</p>
  </BaseLimitModal>
);

const SetupSignoffModal = (props: IntermediateModalProps): JSX.Element => (
  <BaseLimitModal
    title={gt('Upgrade to request documents Sign offs')}
    confirmText={gt('Upgrade Plan Now')}
    {...props}
  >
    <p className="lead">
      {gt('Sign offs are available on pay per team member pricing plans.')}
    </p>
    <p>{gt('Upgrade your account to setup Sign Off rules for your teams.')}</p>
  </BaseLimitModal>
);

const AssignToRoleModal = (props: IntermediateModalProps): JSX.Element => (
  <BaseLimitModal
    title={gt('Upgrade to assign to Roles')}
    confirmText={gt('Upgrade Plan Now')}
    {...props}
  >
    <p>
      {gt(
        'To assign a task to team roles, request a change in your subscription to per user pricing.'
      )}
    </p>
  </BaseLimitModal>
);

/* base modal */
interface BaseLimitModalProps extends IntermediateModalProps {
  children?: React.ReactNode;
  confirmText?: string;
  title: string;
}

const BaseLimitModal = ({
  onCancel,
  show,
  title,
  children,
  confirmText,
  isOwner,
  owner_email,
}: BaseLimitModalProps) => (
  <TabbedModal
    onCancel={onCancel}
    show={show}
    tabs={[
      {
        eventKey: 'upgrade-account-tab',
        name: title,
        icon: 'fa fa-coffee',
        content: (
          <div>
            {children}
            {!isOwner && contactOwnerParagraph(owner_email)}
            <StyledModalFooter>
              <button onClick={onCancel} className="btn btn-link">
                {gt('Cancel')}
              </button>
              {isOwner ? (
                <a href={urls.UPGRADE} className="btn btn-primary">
                  {confirmText} <i className="fa fa-caret-right" />
                </a>
              ) : null}
            </StyledModalFooter>
          </div>
        ),
      },
    ]}
  />
);

const modalsMap = {
  [UPGRADE_MODALS.PROCEDURE_LIMIT]: ProcedureLimitModal,
  [UPGRADE_MODALS.USER_LIMIT]: UserLimitModal,
  [UPGRADE_MODALS.MANAGER_LIMIT]: ManagerLimitModal,
  [UPGRADE_MODALS.TEAM_LIMIT]: TeamsLimitModal,
  [UPGRADE_MODALS.CREATE_KB]: CreateKBModal,
  [UPGRADE_MODALS.SETUP_TEAM_SIGNOFF]: SetupSignoffModal,
  [UPGRADE_MODALS.ASSIGN_TO_ROLE]: AssignToRoleModal,
};

const contactOwnerParagraph = (owner_email: string): JSX.Element => (
  <p>
    {gt('Contact your account owner')}
    &nbsp;<a href={`mailto:${owner_email}`}>{owner_email}</a>
  </p>
);
